import { useRef, useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ImageUploadInput
 *
 * A URL text input paired with an Upload button + optional remove/clear.
 * Users can EITHER paste an external URL OR upload a file. On successful
 * upload, the returned durable public URL is written back via `onChange`.
 *
 * Props:
 *   label         — visible label for the field
 *   value         — current URL string (controlled)
 *   onChange(url) — called with the new URL (or empty string when cleared)
 *   kind          — 'logo' | 'banner' | 'cover' | 'generic' (drives size limit on the server)
 *   showPreview   — if true, show a thumbnail below the input when value is set
 *   previewClassName — Tailwind classes for the preview image
 *   placeholder   — placeholder for the URL input
 *   testId        — data-testid prefix (e.g. 'club-logo')
 */
export default function ImageUploadInput({
  label,
  value = '',
  onChange,
  kind = 'generic',
  showPreview = true,
  previewClassName = 'h-16 w-16 object-cover rounded border',
  placeholder = 'https://... or click Upload',
  testId = 'image-upload',
  disabled = false,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const openPicker = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;

    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/uploads/club-image?kind=${encodeURIComponent(kind)}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        let msg = `Upload failed (HTTP ${res.status})`;
        try {
          const err = await res.json();
          msg = err.detail || msg;
        } catch { /* ignore */ }
        toast.error(msg);
        return;
      }
      const data = await res.json();
      const url = `${API}${data.url}`;
      onChange?.(url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(`Upload failed: ${err.message || 'network error'}`);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    if (disabled) return;
    onChange?.('');
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}
      <div className="flex gap-2">
        <Input
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          disabled={disabled || uploading}
          data-testid={`${testId}-url`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={openPicker}
          disabled={disabled || uploading}
          data-testid={`${testId}-upload-btn`}
          className="shrink-0"
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Uploading
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Upload
            </>
          )}
        </Button>
        {value && !uploading && (
          <Button
            type="button"
            variant="ghost"
            onClick={handleClear}
            disabled={disabled}
            data-testid={`${testId}-clear-btn`}
            title="Clear"
            className="shrink-0 text-muted-foreground hover:text-destructive"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        onChange={handleFile}
        className="hidden"
        data-testid={`${testId}-file-input`}
      />
      {showPreview && value && (
        <div className="mt-2 flex items-center gap-2">
          <img
            src={value}
            alt={label || 'preview'}
            className={previewClassName}
            data-testid={`${testId}-preview`}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <ImageIcon className="w-3 h-3" />
            preview
          </span>
        </div>
      )}
    </div>
  );
}
